import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Paper,
  CircularProgress,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import { numberToWords } from "../utils/numberToWords";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const MySalarySlip = () => {
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [salarySlip, setSalarySlip] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dateOfJoining, setDateOfJoining] = useState(null);
  const [loadingEmployee, setLoadingEmployee] = useState(true);

  // Get current date info
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // Months are 0-indexed

  // Fetch employee details including date of joining when component mounts
  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) return;

        const response = await fetch(
          `${BASE_URL}/api/employees/logineduser/myprofile`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.dateOfJoining) {
            const joiningDate = new Date(data.dateOfJoining);
            setDateOfJoining(joiningDate);

            // Set default year to joining year if current year is same as joining year
            const joiningYear = joiningDate.getFullYear();
            setYear(joiningYear.toString());

            // Set default month to joining month if current year is same as joining year
            if (currentYear === joiningYear) {
              const joiningMonth = joiningDate.getMonth() + 1;
              setMonth(joiningMonth.toString());
            }
          }
        }
      } catch (err) {
        console.error("Error fetching employee details:", err);
      } finally {
        setLoadingEmployee(false);
      }
    };

    fetchEmployeeDetails();
  }, []);

  // Check if selected month/year is in the future
  const isFutureMonth = (selectedMonth, selectedYear) => {
    if (selectedYear > currentYear) return true;
    if (selectedYear === currentYear && selectedMonth > currentMonth)
      return true;
    return false;
  };

  // Generate month options based on joining date and current date
  const getMonthOptions = () => {
    if (!dateOfJoining || !year) return [];

    const selectedYear = parseInt(year, 10);
    const joiningYear = dateOfJoining.getFullYear();
    const joiningMonth = dateOfJoining.getMonth() + 1;

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    let startMonth = 1;
    let endMonth = 12;

    // If selected year is the joining year, start from joining month
    if (selectedYear === joiningYear) {
      startMonth = joiningMonth;
    }

    // If selected year is current year, end at current month
    if (selectedYear === currentYear) {
      endMonth = currentMonth;
    }

    const months = [];
    for (let m = startMonth; m <= endMonth; m++) {
      months.push({
        value: m.toString(),
        label: monthNames[m - 1],
        disabled: isFutureMonth(m, selectedYear),
      });
    }

    return months;
  };

  // Generate year options from joining year to current year
  const getYearOptions = () => {
    if (!dateOfJoining) return [];

    const joiningYear = dateOfJoining.getFullYear();
    const years = [];

    for (let y = joiningYear; y <= currentYear; y++) {
      years.push({
        value: y.toString(),
        label: y.toString(),
      });
    }

    return years;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const selectedMonthNum = parseInt(month, 10);
    const selectedYearNum = parseInt(year, 10);

    // Validate month and year
    if (!month || !year) {
      setError("Please select both month and year");
      return;
    }

    // Check if selected month/year is in the future
    if (isFutureMonth(selectedMonthNum, selectedYearNum)) {
      setError("Cannot generate salary slip for future months");
      return;
    }

    setIsLoading(true);

    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        setError("No authentication token found");
        return;
      }

      const response = await fetch(
        `${BASE_URL}/api/salaryslip/mynewsalaryslip`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ month, year }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch salary slip");
      }

      const data = await response.json();
      setSalarySlip(data);
    } catch (err) {
      setError(err.message || "Failed to fetch salary slip");
      setSalarySlip(null);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = async () => {
    const doc = new jsPDF();

    // Load the logo image
    const logoUrl = require("../../Assets/brandBlack.png"); // Adjust path as needed
    const logoResponse = await fetch(logoUrl);
    const logoBlob = await logoResponse.blob();
    const logoDataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(logoBlob);
    });

    // Add logo to the top right
    doc.addImage(logoDataUrl, "PNG", 150, 10, 40, 20);

    // Company Header
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text("InfyU Labs Pvt. Ltd.", 105, 15, null, null, "center");

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("CIN:U930 00DL2019PTC354588", 105, 22, null, null, "center");
    doc.text(
      "Reg Add - H N. 28, Kh. No. 38/1, 1st Floor,",
      105,
      28,
      null,
      null,
      "center"
    );
    doc.text(
      "Amrit Vihar, BLK-A, Burari, North",
      105,
      34,
      null,
      null,
      "center"
    );
    doc.text("Delhi 110084 IN", 105, 40, null, null, "center");

    // Horizontal line
    doc.setDrawColor(0, 0, 0);
    doc.line(15, 45, 195, 45);

    // Operational Office Address
    doc.setFont("helvetica", "bold");
    doc.text("INFYU LABS PRIVATE LIMITED", 105, 52, null, null, "center");
    doc.setFont("helvetica", "normal");
    doc.text(
      "Operational Office Address - InfyU Labs, C01 and C05, #402, Research Park,",
      105,
      58,
      null,
      null,
      "center"
    );
    doc.text(
      "IIT Gandhinagar, Palaj, Gandhinagar, Gujarat, 382355",
      105,
      64,
      null,
      null,
      "center"
    );

    // Another horizontal line
    doc.line(15, 70, 195, 70);

    // Pay Slip Title
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`Pay Slip for the month of`, 105, 80, null, null, "center");
    doc.text(
      `${salarySlip.month} ${salarySlip.year}`,
      105,
      86,
      null,
      null,
      "center"
    );

    // Employee Details Table - Adjusted to match width of Salary Details Table
    doc.autoTable({
      startY: 95,
      margin: { left: 25 }, // Changed from 15 to 25 to match the second table
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 45, fontStyle: "bold" },
        1: { cellWidth: 30 },
        2: { cellWidth: 45, fontStyle: "bold" },
        3: { cellWidth: 45 },
      },
      body: [
        [
          "Emp ID",
          salarySlip.employeeId,
          "Employee Name:-",
          salarySlip.employeeName,
        ],
        ["PF. No.", "NA", "DOJ : -", salarySlip.dateOfJoining],
        ["Paid / Present Days", salarySlip.fullDays, "", ""],
        ["Location", salarySlip.jobLocation, "", ""],
      ],
    });

    // Salary Details Table - Width matches Employee Details Table
    doc.autoTable({
      startY: doc.autoTable.previous.finalY + 10,
      margin: { left: 25 },
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 45, fontStyle: "bold" },
        1: { cellWidth: 30 },
        2: { cellWidth: 45 },
        3: { cellWidth: 45, fontStyle: "bold" },
      },
      head: [["Earnings", "Rate", "Earned Salary", "Deductions"]],
      body: [
        ["BASIC", "", salarySlip.basic, "TDS"],
        ["HRA", "", salarySlip.hra, salarySlip.TDSAmount],
        ["Special All", "", salarySlip.special, ""],
        ["Total", "", salarySlip.netPayableSalary, "Total"],
        ["Net Pay", "", salarySlip.netPayableSalary, "0"],
        ["In Words", { content: formattedSalaryInWords, colSpan: 3 }, "", ""],
      ],
    });

    // Footer note
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text(
      "* This is a computer generated salary-slip which does not require any signature.",
      105,
      doc.autoTable.previous.finalY + 10,
      null,
      null,
      "center"
    );

    doc.save(
      `SalarySlip_${salarySlip.employeeId}_${salarySlip.month}_${salarySlip.year}.pdf`
    );
  };

  const formattedSalaryInWords = salarySlip?.netPayableSalary
    ? numberToWords(Number(salarySlip.netPayableSalary))
    : "Salary not available";

  if (loadingEmployee) {
    return (
      <PageContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress sx={{ color: "#667eea" }} />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="My Salary Slip"
        subtitle="Generate and download your monthly salary slips easily."
        icon={<ReceiptLongIcon />}
        actions={
          salarySlip ? (
            <Button
              onClick={downloadPDF}
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                borderRadius: "8px",
                px: 2.5,
                py: 0.75,
                boxShadow: "none",
                "&:hover": {
                  background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                  boxShadow: "0 2px 8px rgba(30, 64, 175, 0.3)",
                },
              }}
            >
              Download PDF
            </Button>
          ) : null
        }
      />

      {/* Month/Year Filter Card */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #eef0f6",
          borderRadius: "16px",
          p: 3,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            color: "#1a1a2e",
            pb: 1.5,
            mb: 2.5,
            borderBottom: "2px solid #f0f0f5",
          }}
        >
          Select Pay Period
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="year-label">Year</InputLabel>
                <Select
                  labelId="year-label"
                  id="year"
                  value={year}
                  label="Year"
                  onChange={(e) => {
                    setYear(e.target.value);
                    setMonth("");
                  }}
                  required
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#f5f7fb",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#eef0f6",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#CBD5E1",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                    },
                  }}
                >
                  <MenuItem value="">Select Year</MenuItem>
                  {getYearOptions().map((yearOption) => (
                    <MenuItem key={yearOption.value} value={yearOption.value}>
                      {yearOption.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="month-label">Month</InputLabel>
                <Select
                  labelId="month-label"
                  id="month"
                  value={month}
                  label="Month"
                  onChange={(e) => setMonth(e.target.value)}
                  required
                  disabled={!year}
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#f5f7fb",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#eef0f6",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#CBD5E1",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                    },
                  }}
                >
                  <MenuItem value="">Select Month</MenuItem>
                  {getMonthOptions().map((monthOption) => (
                    <MenuItem
                      key={monthOption.value}
                      value={monthOption.value}
                      disabled={monthOption.disabled}
                    >
                      {monthOption.label}
                      {monthOption.disabled && " (Not available)"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading || !month || !year}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                  ) : (
                    <SearchIcon />
                  )
                }
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderRadius: "8px",
                  py: 1,
                  boxShadow: "none",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                    boxShadow: "0 2px 8px rgba(30, 64, 175, 0.3)",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#CBD5E1",
                    color: "#fff",
                  },
                }}
              >
                {isLoading ? "Generating..." : "Generate Slip"}
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: "8px",
                bgcolor: "#FEF2F2",
                border: "1px solid #FECACA",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#DC2626", fontWeight: 500, fontSize: "0.8rem" }}
              >
                {error}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Salary Slip Details */}
      {salarySlip && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            p: 3,
          }}
        >
          {/* Employee Info Header */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "#1a1a2e",
              pb: 1.5,
              mb: 2,
              borderBottom: "2px solid #f0f0f5",
            }}
          >
            Employee Information
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(5, 1fr)",
              },
              gap: 2,
              mb: 3,
              p: 2,
              bgcolor: "#f5f7fb",
              borderRadius: "8px",
              border: "1px solid #f0f2f8",
            }}
          >
            {[
              { label: "Employee", value: salarySlip.employeeName, sub: salarySlip.employeeId },
              { label: "Designation", value: salarySlip.designation },
              { label: "Pay Period", value: `${salarySlip.month} ${salarySlip.year}` },
              { label: "Location", value: salarySlip.jobLocation },
              { label: "Date of Joining", value: salarySlip.dateOfJoining },
            ].map((item, index) => (
              <Box key={index}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#8b8fa3",
                    fontWeight: 500,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#1a1a2e",
                    fontSize: "0.85rem",
                    mt: 0.25,
                  }}
                >
                  {item.value}
                </Typography>
                {item.sub && (
                  <Typography
                    variant="caption"
                    sx={{ color: "#8b8fa3", fontSize: "0.75rem" }}
                  >
                    {item.sub}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          {/* Attendance Summary */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "#1a1a2e",
              pb: 1.5,
              mb: 2,
              borderBottom: "2px solid #f0f0f5",
            }}
          >
            Attendance Summary
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            {[
              { label: "Total Days", value: salarySlip.totalDaysInMonth },
              { label: "Full Days", value: salarySlip.fullDays },
              { label: "Half Days", value: salarySlip.halfDays },
              { label: "Absent Days", value: salarySlip.absentDays },
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  bgcolor: "#f5f7fb",
                  borderRadius: "8px",
                  border: "1px solid #f0f2f8",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#8b8fa3",
                    fontWeight: 500,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#1a1a2e",
                    fontSize: "1.25rem",
                    mt: 0.5,
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Earnings & Deductions side by side */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Earnings Table */}
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#1a1a2e",
                  pb: 1.5,
                  mb: 2,
                  borderBottom: "2px solid #f0f0f5",
                }}
              >
                Earnings
              </Typography>

              <Box sx={{ border: "1px solid #eef0f6", borderRadius: "8px", overflow: "hidden" }}>
                {/* Table Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    bgcolor: "#f5f7fb",
                    borderBottom: "2px solid #f0f0f5",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#8b8fa3", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                    Component
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#8b8fa3", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                    Amount
                  </Typography>
                </Box>
                {/* Table Rows */}
                {[
                  ["Basic Salary", salarySlip.basic],
                  ["HRA", salarySlip.hra],
                  ["Special Allowance", salarySlip.special],
                  ["Bonus", salarySlip.bonus],
                ].map(([label, value], i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1.25,
                      borderBottom: i < 3 ? "1px solid #f0f2f8" : "none",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#5a5f7a", fontSize: "0.85rem" }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a2e", fontSize: "0.85rem" }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
                {/* Gross Salary */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                    bgcolor: "#F0FDF4",
                    borderTop: "1px solid #BBF7D0",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: "#15803D", fontSize: "0.875rem" }}>
                    Gross Salary
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: "#15803D", fontSize: "0.875rem" }}>
                    {salarySlip.monthlySalary}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Deductions Table */}
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#1a1a2e",
                  pb: 1.5,
                  mb: 2,
                  borderBottom: "2px solid #f0f0f5",
                }}
              >
                Deductions
              </Typography>

              <Box sx={{ border: "1px solid #eef0f6", borderRadius: "8px", overflow: "hidden" }}>
                {/* Table Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    bgcolor: "#f5f7fb",
                    borderBottom: "2px solid #f0f0f5",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#8b8fa3", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                    Component
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#8b8fa3", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                    Amount
                  </Typography>
                </Box>
                {/* Table Rows */}
                {[
                  ["TDS", salarySlip.TDSAmount],
                  ["Leave Deduction", salarySlip.leavededuction],
                ].map(([label, value], i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1.25,
                      borderBottom: i < 1 ? "1px solid #f0f2f8" : "none",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#5a5f7a", fontSize: "0.85rem" }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a2e", fontSize: "0.85rem" }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
                {/* Total Deductions */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                    bgcolor: "#FEF2F2",
                    borderTop: "1px solid #FECACA",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: "#DC2626", fontSize: "0.875rem" }}>
                    Total Deductions
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: "#DC2626", fontSize: "0.875rem" }}>
                    {salarySlip.TDSAmount + salarySlip.leavededuction}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Net Pay Summary */}
          <Box
            sx={{
              bgcolor: "#eef0ff",
              borderRadius: "16px",
              p: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 1,
              border: "1px solid #DBEAFE",
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#8b8fa3",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Net Payable Salary
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#667eea",
                  fontSize: { xs: "1.5rem", sm: "1.75rem" },
                  lineHeight: 1.2,
                }}
              >
                {salarySlip.netPayableSalary}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#8b8fa3",
                  fontStyle: "italic",
                  mt: 0.5,
                  fontSize: "0.8rem",
                }}
              >
                ({formattedSalaryInWords})
              </Typography>
            </Box>
            <Button
              onClick={downloadPDF}
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                borderRadius: "8px",
                px: 2.5,
                py: 0.75,
                boxShadow: "none",
                flexShrink: 0,
                "&:hover": {
                  background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                  boxShadow: "0 2px 8px rgba(30, 64, 175, 0.3)",
                },
              }}
            >
              Download PDF
            </Button>
          </Box>
        </Paper>
      )}
    </PageContainer>
  );
};

export default MySalarySlip;
